import {
  occurrenceLabel,
  resolveOccurrenceColor,
  resolveOccurrenceIcon
} from "./occurrenceVisual";

export const DEFAULT_NIVEL = 1;

function toNumberMap(value) {
  const out = {};
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return out;
  }
  Object.keys(value).forEach(key => {
    const nivel = Number(value[key]);
    if (nivel === 1 || nivel === 2 || nivel === 3) {
      out[String(key)] = nivel;
    }
  });
  return out;
}

function toStringSet(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(item => String(item)).filter(Boolean);
}

function toNivelList(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(item => Number(item))
    .filter(nivel => nivel === 1 || nivel === 2 || nivel === 3);
}

function mapHasNivel(map, nivel) {
  return (
    Object.keys(map.groups || {}).some(key => map.groups[key] === nivel) ||
    Object.keys(map.types || {}).some(key => map.types[key] === nivel)
  );
}

function normalizeFormConfig(form) {
  const source = form && typeof form === "object" ? form : {};
  const modeFor = (field, fallback) => {
    const mode = String(source[field] || fallback).toLowerCase();
    if (mode === "required" || mode === "optional" || mode === "hidden") {
      return mode;
    }
    return fallback;
  };
  return {
    discipline: modeFor("discipline", "optional"),
    stage: modeFor("stage", "optional"),
    lesson: modeFor("lesson", "hidden"),
    attachments: modeFor("attachments", "optional"),
    observations: modeFor("observations", "optional")
  };
}

const trimmedString = value => (value == null ? "" : String(value).trim());

function normalizeRole(value) {
  const role = String(value == null ? "" : value)
    .trim()
    .toUpperCase();
  if (!role || role.length > 40 || role.indexOf(",") >= 0) {
    return "";
  }
  return role;
}

function toRoleList(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return [...new Set(value.map(normalizeRole).filter(Boolean))];
}

function toHideAuthoredBy(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const hiddenByViewer = {};
  Object.keys(value).forEach(key => {
    const viewer = normalizeRole(key);
    const authors = toRoleList(value[key]);
    if (viewer && authors.length) {
      hiddenByViewer[viewer] = authors;
    }
  });
  return hiddenByViewer;
}

export function hiddenAuthorRolesForViewer(config, viewerTipo) {
  const viewer =
    String(viewerTipo || "")
      .trim()
      .toUpperCase() || "PROFESSOR";
  const map = config && config.hideAuthoredBy;
  const authors = map && map[viewer];
  return Array.isArray(authors) ? authors.slice() : [];
}

export function parseAdvancedConfig(raw) {
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      parsed = {};
    }
  }
  const cfg = parsed && typeof parsed === "object" ? parsed : {};
  return {
    groups: toNumberMap(cfg.groups),
    types: toNumberMap(cfg.types),
    niveis: toNivelList(cfg.niveis),
    alertGroups: toStringSet(cfg.alertGroups),
    alertTypes: toStringSet(cfg.alertTypes),
    form: normalizeFormConfig(cfg.form),
    hideAuthoredBy: toHideAuthoredBy(cfg.hideAuthoredBy),
    viewerTipo: String(cfg.viewerTipo || "").toUpperCase()
  };
}

export function filterGroupsByIds(groups, idGroups) {
  const allowed = new Set((idGroups || []).map(trimmedString));
  return (groups || []).filter(group =>
    allowed.has(trimmedString(group && group.idGroup))
  );
}

function toGroupIdList(value) {
  const items = Array.isArray(value) ? value : String(value).split(",");
  return [...new Set(items.map(trimmedString).filter(Boolean))];
}

export function lockedGroupIdsFromLocation(location) {
  const fromQuery = new URLSearchParams(
    (location && location.search) || ""
  ).get("group");
  const fromState = location && location.state && location.state.group;
  const raw = fromQuery != null && fromQuery !== "" ? fromQuery : fromState;
  if (raw == null) {
    return null;
  }
  const ids = toGroupIdList(raw);
  return ids.length ? ids : null;
}

export function resolveViewerTipo(externalData, config) {
  if (config && config.viewerTipo) {
    return config.viewerTipo;
  }
  if (externalData && externalData.isCoordinator) {
    return "COORDENADOR";
  }
  return "PROFESSOR";
}

export function visibleNiveisForViewer(viewerTipo) {
  const tipo = String(viewerTipo || "").toUpperCase();
  if (tipo === "COORDENADOR") {
    return [1, 2, 3];
  }
  if (tipo === "SUPERVISOR") {
    return [1, 2];
  }
  return [1, 2];
}

export {
  occurrenceLabel,
  resolveOccurrenceIcon,
  resolveOccurrenceColor
} from "./occurrenceVisual";

// Contrato TOTVS: SOCORRENCIAALUNO.OBSERVACOES guarda pública + interna.
const INTERNAL_NOTES_MARK = "#INT#";
const LAB_SEED_MARK = "#WBSCR829#";

export function splitOccurrenceNotes(raw) {
  const text = String(raw == null ? "" : raw).replace(LAB_SEED_MARK, "");
  const idx = text.indexOf(INTERNAL_NOTES_MARK);
  if (idx < 0) {
    return { notes: text.trim(), internalNotes: "" };
  }
  return {
    notes: text.slice(0, idx).trim(),
    internalNotes: text
      .slice(idx + INTERNAL_NOTES_MARK.length)
      .replace(/^\n/, "")
      .trim()
  };
}

export function joinOccurrenceNotes(notes, internalNotes) {
  const publicText = String(notes || "").trim();
  const internal = String(internalNotes || "").trim();
  if (!internal) {
    return publicText;
  }
  if (!publicText) {
    return `${INTERNAL_NOTES_MARK}\n${internal}`;
  }
  return `${publicText}\n${INTERNAL_NOTES_MARK}\n${internal}`;
}

export function filterOccurrences(occurrences, query) {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) {
    return occurrences || [];
  }
  return (occurrences || []).filter(item => {
    const hay = [
      item.studentName,
      item.reason,
      item.title,
      item.description,
      item.stage,
      item.stageShortName,
      item.teacher
    ]
      .map(value => String(value || "").toLowerCase())
      .join(" ");
    return hay.indexOf(q) >= 0;
  });
}

export function isNivelEnabled(config, nivel) {
  const map = config || parseAdvancedConfig({});
  if (map.niveis && map.niveis.length) {
    return nivel === 1 || map.niveis.indexOf(nivel) >= 0;
  }
  if (nivel === 2) {
    return mapHasNivel(map, 2);
  }
  return true;
}

/** Nível 2/3 vem do JSON do campus, não de coluna nativa do RM. */
export function isFrequencyAbsence(occurrence) {
  const title = String((occurrence && occurrence.title) || "")
    .trim()
    .toUpperCase();
  if (title === "FALTA") {
    return true;
  }
  const label = occurrenceLabel(occurrence)
    .trim()
    .toUpperCase();
  return label === "FALTA" || label.endsWith(" - FALTA");
}

export function resolveNivel(occurrence, config) {
  if (isFrequencyAbsence(occurrence)) {
    return DEFAULT_NIVEL;
  }
  const map = config || parseAdvancedConfig({});
  const typeKey =
    occurrence && occurrence.idType != null ? String(occurrence.idType) : "";
  const groupKey =
    occurrence && occurrence.idGroup != null ? String(occurrence.idGroup) : "";
  let nivel = DEFAULT_NIVEL;
  if (typeKey && map.types[typeKey] != null) {
    nivel = map.types[typeKey];
  } else if (groupKey && map.groups[groupKey] != null) {
    nivel = map.groups[groupKey];
  } else {
    const fromRow = Number(occurrence && occurrence.nivel);
    if (fromRow === 1 || fromRow === 2 || fromRow === 3) {
      nivel = fromRow;
    }
  }
  return isNivelEnabled(map, nivel) ? nivel : DEFAULT_NIVEL;
}

export function isAlertOccurrence(occurrence, config) {
  const map = config || parseAdvancedConfig({});
  const typeKey =
    occurrence && occurrence.idType != null ? String(occurrence.idType) : "";
  const groupKey =
    occurrence && occurrence.idGroup != null ? String(occurrence.idGroup) : "";
  if (typeKey && map.alertTypes.indexOf(typeKey) >= 0) {
    return true;
  }
  if (groupKey && map.alertGroups.indexOf(groupKey) >= 0) {
    return true;
  }
  return Boolean(occurrence && occurrence.alert);
}

export function stackKey(occurrence) {
  const group =
    occurrence && occurrence.idGroup != null ? String(occurrence.idGroup) : "";
  const type =
    occurrence && occurrence.idType != null ? String(occurrence.idType) : "";
  return `${group}:${type}`;
}

function occurrenceTime(occurrence) {
  const raw = occurrence && occurrence.date;
  if (raw && typeof raw.valueOf === "function") {
    const viaValueOf = raw.valueOf();
    if (typeof viaValueOf === "number" && Number.isFinite(viaValueOf)) {
      return viaValueOf;
    }
  }
  const value = new Date(raw).getTime();
  return Number.isFinite(value) ? value : 0;
}

export function decorateOccurrences(occurrences, config, primaryColor) {
  const map = config || parseAdvancedConfig({});
  return (occurrences || []).map(occurrence => {
    const decorated = {
      ...occurrence,
      nivel: resolveNivel(occurrence, map),
      alert: isAlertOccurrence(occurrence, map)
    };
    return {
      ...decorated,
      icon: resolveOccurrenceIcon(decorated),
      color: resolveOccurrenceColor(decorated, primaryColor)
    };
  });
}

function stacksForNivel(items, nivel) {
  const filtered = items.filter(item => item.nivel === nivel);
  const byKey = {};
  filtered.forEach(item => {
    const key = stackKey(item);
    if (!byKey[key]) {
      byKey[key] = [];
    }
    byKey[key].push(item);
  });
  return Object.keys(byKey).map(key => {
    const list = byKey[key];
    return {
      key,
      latest: list[0],
      rest: list.slice(1),
      count: list.length
    };
  });
}

export function buildAdvancedSections(occurrences, config, options) {
  const opts = options || {};
  const decorated = decorateOccurrences(occurrences, config, opts.primaryColor)
    .slice()
    .sort((a, b) => occurrenceTime(b) - occurrenceTime(a));
  const visible = opts.visibleNiveis || [1, 2, 3];
  return {
    level3: visible.indexOf(3) >= 0 ? stacksForNivel(decorated, 3) : [],
    level2: visible.indexOf(2) >= 0 ? stacksForNivel(decorated, 2) : [],
    list: decorated
  };
}

function isBlankStageId(value) {
  const id = String(value == null ? "" : value).trim();
  return !id || id === "-" || id === "—";
}

function startOfDayTime(value) {
  if (value == null || value === "") {
    return null;
  }
  const asDate = value && value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(asDate.getTime())) {
    return null;
  }
  return new Date(
    asDate.getFullYear(),
    asDate.getMonth(),
    asDate.getDate()
  ).getTime();
}

export function findStageForDate(stages, date) {
  const when = startOfDayTime(date);
  if (when == null) {
    return null;
  }
  return (
    (stages || []).find(stage => {
      const start = startOfDayTime(stage.start);
      const end = startOfDayTime(stage.end);
      if (start == null || end == null) {
        return false;
      }
      return when >= start && when <= end;
    }) || null
  );
}

export function findStageForOccurrence(occurrence, stages) {
  if (isBlankStageId(occurrence && occurrence.idStage)) {
    return null;
  }
  const id = String(occurrence.idStage);
  return (stages || []).find(stage => String(stage.id) === id) || null;
}

export function reduceStageName(name) {
  const raw = String(name || "").trim();
  if (!raw) {
    return "";
  }
  if (raw.length <= 8) {
    return raw;
  }
  return raw
    .replace(/\s*[—–-].*$/, "")
    .replace(/imestre/gi, "im")
    .replace(/rimestre/gi, "ri")
    .replace(/emestre/gi, "em")
    .trim();
}

export function resolveStageShortName(occurrence, stages) {
  if (occurrence && occurrence.stageShortName) {
    return String(occurrence.stageShortName).trim();
  }
  const stage = findStageForOccurrence(occurrence, stages);
  if (stage && stage.shortName) {
    return String(stage.shortName).trim();
  }
  if (stage && stage.name) {
    return reduceStageName(stage.name);
  }
  if (occurrence && occurrence.stage && !isBlankStageId(occurrence.stage)) {
    return reduceStageName(occurrence.stage);
  }
  return "";
}

export function withStageFields(occurrences, stages) {
  return (occurrences || []).map(occurrence => {
    const stage = findStageForOccurrence(occurrence, stages);
    const stageName =
      (stage && stage.name) ||
      (occurrence.stage && !isBlankStageId(occurrence.stage)
        ? occurrence.stage
        : "");
    return {
      ...occurrence,
      stage: stageName || occurrence.idStage,
      stageShortName: resolveStageShortName(
        { ...occurrence, stage: stageName },
        stages
      )
    };
  });
}

export function fieldMode(formConfig, field) {
  const form = (formConfig && formConfig.form) || normalizeFormConfig({});
  return form[field] || "optional";
}

export function isFieldHidden(formConfig, field) {
  return fieldMode(formConfig, field) === "hidden";
}

export function isFieldRequired(formConfig, field) {
  return fieldMode(formConfig, field) === "required";
}

const ADVANCED_CRUD_ACTIONS = ["view", "create", "edit", "delete"];

export function canAdvancedOccurrence(ability, action) {
  if (!ability) {
    return false;
  }
  const hasGranular = ADVANCED_CRUD_ACTIONS.some(item =>
    ability.can(item, "diaryOccurrenceAdvanced")
  );
  if (hasGranular) {
    if (ability.can(action, "diaryOccurrenceAdvanced")) {
      return true;
    }
    return (
      action === "view" && ability.can("use module", "diaryOccurrenceAdvanced")
    );
  }
  if (!ability.can("use module", "diaryOccurrenceAdvanced")) {
    return false;
  }
  if (action === "view") {
    return true;
  }
  return ability.can("save", "diaryOccurrences");
}

export function canCreateOccurrence(ability, advanced) {
  if (advanced) {
    return canAdvancedOccurrence(ability, "create");
  }
  return !!(ability && ability.can("save", "diaryOccurrences"));
}
